import { supabase } from './supabase';

export interface Chemical {
  id: string;
  pubchem_cid: string;
  name: string;
  iupac_name: string | null;
  molecular_formula: string | null;
  status: 'halal' | 'haram' | 'mushbooh';
  source: string | null;
  description: string | null;
  safety_notes: string | null;
}

// Cache configuration
const CACHE_KEY = 'chemicals_cache';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

interface CacheEntry {
  timestamp: number;
  data: Chemical[];
}

// Get all chemicals from cache or database
export async function getAllChemicals(): Promise<Chemical[]> {
  // Check cache first
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    const { timestamp, data }: CacheEntry = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  // Fetch from Supabase if cache miss or expired
  const { data, error } = await supabase
    .from('chemicals')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching chemicals:', error);
    throw new Error('Failed to fetch chemicals');
  }

  // Update cache
  localStorage.setItem(CACHE_KEY, JSON.stringify({
    timestamp: Date.now(),
    data
  }));

  return data;
}

// Match chemical names in ingredient text with more precise matching
function findChemicalMatches(ingredient: string, chemicals: Chemical[]): Chemical[] {
  const lowerIngredient = ingredient.toLowerCase().trim();
  
  // Skip common safe ingredients
  if (
    lowerIngredient.includes('vegetable') ||
    lowerIngredient.includes('plant') ||
    lowerIngredient === 'sugar' ||
    lowerIngredient === 'salt' ||
    lowerIngredient === 'water' ||
    lowerIngredient === 'acid' ||
    lowerIngredient === 'syrup' ||
    lowerIngredient === 'wheat flour' ||
    lowerIngredient === 'flour' ||
    lowerIngredient === 'raising agent' ||
    lowerIngredient === 'sodium bicarbonate' ||
    lowerIngredient === 'citric acid' ||
    lowerIngredient === 'cinnamon' ||
    lowerIngredient.includes('lecithin') // Soy lecithin is halal
  ) {
    return [];
  }

  return chemicals.filter(chemical => {
    const names = [
      chemical.name,
      chemical.iupac_name,
      chemical.molecular_formula
    ].filter((name): name is string => typeof name === 'string');

    return names.some(name => {
      const lowerName = name.toLowerCase();
      
      // Exact match only for critical chemicals
      if (
        chemical.status === 'haram' ||
        name.length < 6 // Short names need exact match
      ) {
        return lowerIngredient === lowerName;
      }

      // For other chemicals, require word boundary matches
      const wordBoundaryPattern = new RegExp(`\\b${lowerName}\\b`);
      return wordBoundaryPattern.test(lowerIngredient);
    });
  });
}

// Analyze ingredients for chemicals
export async function analyzeChemicals(ingredients: string[]): Promise<{
  haramChemicals: Chemical[];
  mushboohChemicals: Chemical[];
  allChemicals: Chemical[];
}> {
  const chemicals = await getAllChemicals();
  const foundChemicals = new Set<Chemical>();

  ingredients.forEach(ingredient => {
    const matches = findChemicalMatches(ingredient, chemicals);
    matches.forEach(chemical => foundChemicals.add(chemical));
  });

  const matchedChemicals = Array.from(foundChemicals);

  return {
    haramChemicals: matchedChemicals.filter(c => c.status === 'haram'),
    mushboohChemicals: matchedChemicals.filter(c => c.status === 'mushbooh'),
    allChemicals: matchedChemicals
  };
}